




















import 'quill/dist/quill.snow.css'
import Quill from 'quill'

import { computed, defineComponent, onMounted, ref } from '@vue/composition-api'
import QuillImageDialog from '@/components/common/quill/QuillImageDialog.vue'
import QuillLinkDialog from '@/components/common/quill/QuillLinkDialog.vue'
import QuillVideoDialog from '@/components/common/quill/QuillVideoDialog.vue'
import type { PropType } from '@vue/composition-api'
import type { QuillOptionsStatic, SelectionChangeHandler, TextChangeHandler } from 'quill'
import type { RangeStatic } from 'quill'
import type { TranslateResult } from 'vue-i18n'

export default defineComponent({
  name: 'HtmlEditor',

  components: { QuillImageDialog, QuillLinkDialog, QuillVideoDialog },

  props: {
    value: {
      type: String,
      default: ''
    },
    label: {
      type: [] as PropType<string | TranslateResult>,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    editorOptions: {
      type: Object as PropType<QuillOptionsStatic>,
      default: null
    },
    errorMessage: {
      type: String,
      default: ''
    }
  },

  emits: {
    'input': (_body: string): boolean => true,
    'update:cursor-position': (_: RangeStatic): boolean => true,
    'update:quill': (_: Quill): boolean => true
  },

  setup(props, { emit }) {
    const quillEditorRef = ref<any>(null)

    const toolbar: Record<string, any> = [
      [{ header: [false, 1, 2, 3, 4, 5, 6] }],
      [{ font: [] }],
      [
        'bold',
        'italic',
        'underline',
        'strike',
        'blockquote',
        'code-block',
        { color: [] },
        { background: [] },
        { script: 'sub' },
        { script: 'super' }
      ],
      [{ align: [] }],
      [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
      ['link', 'image', 'pdf', 'video','table'],
      [{ direction: 'rtl' }],
      ['clean']
    ]

    const quillOptions: QuillOptionsStatic = {
      theme: 'snow',
      modules: { toolbar },
      ...props.editorOptions
    }

    let quill: Quill

    const openedDialog = ref<'link' | 'image' | 'pdf' | 'video' | 'table'| null>(null)

    const linkDialog = computed({
      get: () => openedDialog.value === 'link',
      set: (val) => (openedDialog.value = val ? 'link' : null)
    })

    const imageDialog = computed({
      get: () => openedDialog.value === 'image',
      set: (val) => (openedDialog.value = val ? 'image' : null)
    })

    const pdfDialog = computed({
      get: () => openedDialog.value === 'pdf',
      set: (val) => (openedDialog.value = val ? 'pdf' : null)
    })

    const videoDialog = computed({
      get: () => openedDialog.value === 'video',
      set: (val) => (openedDialog.value = val ? 'video' : null)
    })
    const tableDialog = computed({
      get: () => openedDialog.value === 'table',
      set: (val) => (openedDialog.value = val ? 'table' : null)
    })

//     const tableHandler = () => {
//     console.log("sssssss")
//   const tableHtml = `
//     <table>
//       <tr>
//         <td>Cell 1</td>
//         <td>Cell 2</td>
//       </tr>
//       <tr>
//         <td>Cell 3</td>
//         <td>Cell 4</td>
//       </tr>
//     </table>
//   `;
//   const index = quill.getSelection()?.index || 0;
//   quill.clipboard.dangerouslyPasteHTML(index, tableHtml);
// }

    const imageHandler = () => (openedDialog.value = 'image')
    const linkHandler = (val: unknown) => (val ? (openedDialog.value = 'link') : quill.format('link', null))
    const pdfHandler = () => (openedDialog.value = 'pdf')
    const videoHandler = () => (openedDialog.value = 'video')
    const tableHandler = () => (openedDialog.value = 'table')

    const onImageApplied = (url: string) => {
      quill.focus()
      quill.insertEmbed(quill.getSelection()?.index || 0, 'image', url)
      quill.setSelection(quill.getLength(), 0)
      imageDialog.value = false
    }

    const onLinkApplied = (url: string) => {
      quill.format('link', url)
      linkDialog.value = false
    }
    // this function also working

//     const onPdfApplied = (data:any) => {
//   const { url, name } = data;
//   quill.focus();
//   const selection = quill.getSelection()?.index || 0;
//   quill.insertText(selection, name, 'link', url);
//   quill.setSelection(selection + name.length, 0);
//   videoDialog = false;
// };

const onPdfApplied = (data:any) => {
  const { url, name } = data;
  const displayText = `${name} Download`; // Name of PDF + static text
  
  quill.focus();
  const selection = quill.getSelection()?.index || 0;
  
  // Insert the PDF name with the static text
  quill.insertText(selection, displayText);
  
  // Format the inserted text as a link with the desired URL and class
  quill.formatText(selection, selection + displayText.length, {
    link: url,
    class: 'pdf_file'
  });

  quill.setSelection(selection + displayText.length, 0);
  //videoDialog = false;
};

    const onTextChange: TextChangeHandler = () => emit('input', quill.root.innerHTML)
    const onSelectionChange: SelectionChangeHandler = (cursor) => {
      if (openedDialog.value) {
        return
      }
      emit('update:cursor-position', cursor || { index: quill.getLength(), length: 0 })
    }

    onMounted(() => {
      quill = new Quill(quillEditorRef.value, { ...quillOptions })

      quill.clipboard?.dangerouslyPasteHTML(props.value)

      quill.on('text-change', onTextChange)
      quill.on('selection-change', onSelectionChange)

      const toolbar = quill.getModule('toolbar')
      if (toolbar) {
        toolbar.addHandler('image', imageHandler)
        toolbar.addHandler('link', linkHandler)
        toolbar.addHandler('pdf', pdfHandler)
        toolbar.addHandler('video', videoHandler)
        toolbar.addHandler('table', tableHandler)
      }

      emit('update:quill', quill)
    })

    return {
      quillEditorRef,
      linkDialog,
      imageDialog,
      pdfDialog,
      videoDialog,
      onLinkApplied,
      onImageApplied,
      onPdfApplied,
      tableDialog
    }
  }
})
